"use client";

type HistoryRow = {
  epoch: number;
  train_loss: number;
  validation_loss: number;
};

type C2stFamily = {
  auroc_mean: number;
  auroc_std: number;
  gate_value: number | null;
};

type BatteryReport = {
  epoch: number;
  selected_validation_loss: number | null;
  c2st: Record<"low_level" | "high_level" | "profile_aware" | "condition_only", C2stFamily>;
  structural_invariants_pass: boolean;
  total_response_gev: { generated: number; truth: number; wasserstein: number };
  zero_response_fraction: { generated: number; truth: number };
  shower_fragmentation: {
    gap_fraction_generated: number;
    gap_fraction_truth: number;
    mean_gaps_generated: number;
    mean_gaps_truth: number;
    connected_components_generated: number;
    connected_components_truth: number;
  };
  radial_rms_mm: { generated: number; truth: number };
  mean_longitudinal_profile_relative_l1: number;
  scientific_status: string;
  pairs: number;
};

export type InProgressData = {
  status: string;
  note: string;
  run_tag: string;
  physics_validation_established: boolean;
  updated_utc: string;
  epochs_observed: number;
  latest_epoch: number;
  best_epoch: number;
  best_validation_loss: number;
  all_invariants_pass: boolean;
  all_visualization_qa_pass: boolean;
  history: HistoryRow[];
  battery_reports: BatteryReport[];
};

const EPOCHS_DECLARED = 24;

function LossCurve({ history, bestEpoch }: { history: HistoryRow[]; bestEpoch: number }) {
  const width = 620;
  const height = 220;
  const margin = { left: 56, right: 16, top: 16, bottom: 26 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = history.map((row) => row.validation_loss);
  const trainValues = history.map((row) => row.train_loss);
  const all = [...values, ...trainValues];
  const low = Math.min(...all);
  const high = Math.max(...all);
  const span = Math.max(high - low, 1e-9);
  const xFor = (index: number) =>
    margin.left + (index / Math.max(history.length - 1, 1)) * plotWidth;
  const yFor = (value: number) =>
    margin.top + (1 - (value - low) / span) * plotHeight;
  const pathFor = (series: number[]) =>
    series
      .map((value, index) => `${index === 0 ? "M" : "L"}${xFor(index).toFixed(2)},${yFor(value).toFixed(2)}`)
      .join(" ");
  const ticks = [0, 0.5, 1].map((fraction) => low + fraction * span);
  const bestIndex = history.findIndex((row) => row.epoch === bestEpoch);

  return (
    <svg
      className="line-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Training and validation loss by epoch, in progress"
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={margin.left} x2={width - margin.right} y1={yFor(tick)} y2={yFor(tick)} />
          <text x={margin.left - 8} y={yFor(tick)} textAnchor="end" dominantBaseline="middle">
            {tick.toFixed(3)}
          </text>
        </g>
      ))}
      <path d={pathFor(trainValues)} stroke="var(--muted)" />
      <path d={pathFor(values)} stroke="var(--cyan)" />
      {bestIndex >= 0 && (
        <circle cx={xFor(bestIndex)} cy={yFor(values[bestIndex])} r={4} className="best-epoch-marker" />
      )}
      <text className="axis-title" x={margin.left} y="12">
        loss
      </text>
      <text className="axis-label" x={width - margin.right} y={height - 4} textAnchor="end">
        epoch (0–{history.at(-1)?.epoch ?? 0} of {EPOCHS_DECLARED} declared)
      </text>
    </svg>
  );
}

function Auroc({ label, family, note }: { label: string; family: C2stFamily; note?: string }) {
  const pct = Math.max(0, Math.min(1, family.auroc_mean));
  const gateFraction = family.gate_value ?? null;
  return (
    <div className="auroc-row">
      <div className="auroc-row__label">
        <strong>{label}</strong>
        {note && <small>{note}</small>}
      </div>
      <div className="auroc-row__bar" aria-hidden="true">
        <div className="auroc-row__fill" style={{ width: `${pct * 100}%` }} />
        <div className="auroc-row__chance" style={{ left: "50%" }} title="chance (0.5)" />
        {gateFraction !== null && (
          <div
            className="auroc-row__gate"
            style={{ left: `${gateFraction * 100}%` }}
            title={`gate ${gateFraction.toFixed(2)}`}
          />
        )}
      </div>
      <code>{family.auroc_mean.toFixed(3)} ± {family.auroc_std.toFixed(3)}</code>
    </div>
  );
}

export function InProgressStatus({ data }: { data: InProgressData }) {
  const latestBattery = data.battery_reports.at(-1);
  return (
    <section className="panel in-progress-panel" aria-labelledby="in-progress-heading">
      <div className="in-progress-banner">
        <strong>{data.status}</strong>
        <span>{data.note}</span>
      </div>
      <h3 id="in-progress-heading">
        {data.run_tag} — epoch {data.latest_epoch} of {EPOCHS_DECLARED} declared
      </h3>
      <div className="stats-grid" aria-label="In-progress epoch summary">
        <div className="stat-card">
          <span>Epochs observed</span>
          <strong>{data.epochs_observed} / {EPOCHS_DECLARED}</strong>
        </div>
        <div className="stat-card stat-card--accent">
          <span>Best validation loss (declared selection rule)</span>
          <strong>{data.best_validation_loss.toFixed(4)}</strong>
          <small>epoch {data.best_epoch}</small>
        </div>
        <div className="stat-card">
          <span>Structural invariants</span>
          <strong>{data.all_invariants_pass ? "pass, every epoch" : "FAILED — see logs"}</strong>
        </div>
        <div className="stat-card">
          <span>Test events used</span>
          <strong>0</strong>
        </div>
      </div>
      <LossCurve history={data.history} bestEpoch={data.best_epoch} />
      <div className="chart-legend">
        <span><i style={{ backgroundColor: "var(--cyan)" }} />validation loss</span>
        <span><i style={{ backgroundColor: "var(--muted)" }} />train loss</span>
      </div>

      {latestBattery && (
        <div className="in-progress-battery">
          <h4>
            Classifier two-sample test (C2ST) — checkpoint epoch {latestBattery.epoch}
            {latestBattery.epoch !== data.best_epoch && (
              <small> (not the current best — epoch {data.best_epoch} not yet evaluated)</small>
            )}
          </h4>
          <p className="hero-copy">
            A held-out classifier trying to tell generated showers apart from real Geant4
            ones. 0.5 = indistinguishable (the goal); the project's gate for the
            high_level family is 0.65. {latestBattery.pairs.toLocaleString()} paired
            validation events, three evaluator seeds.
          </p>
          <Auroc label="low_level" family={latestBattery.c2st.low_level} note="raw cell vector — hardest test" />
          <Auroc label="high_level" family={latestBattery.c2st.high_level} note="9 summary stats — the standing gate" />
          <Auroc label="profile_aware" family={latestBattery.c2st.profile_aware} note="per-layer profile" />
          <Auroc label="condition_only" family={latestBattery.c2st.condition_only} note="sanity control — should sit at chance" />

          <h4>Classic ZDC physics metrics vs. Geant4</h4>
          <div className="stats-grid" aria-label="Physics comparison">
            <div className="stat-card">
              <span>Total response (GeV)</span>
              <strong>{latestBattery.total_response_gev.generated.toFixed(2)}</strong>
              <small>Geant4 {latestBattery.total_response_gev.truth.toFixed(2)}</small>
            </div>
            <div className="stat-card">
              <span>Zero-response rate</span>
              <strong>{(latestBattery.zero_response_fraction.generated * 100).toFixed(2)}%</strong>
              <small>Geant4 {(latestBattery.zero_response_fraction.truth * 100).toFixed(2)}%</small>
            </div>
            <div className="stat-card">
              <span>Shower gap fraction</span>
              <strong>{(latestBattery.shower_fragmentation.gap_fraction_generated * 100).toFixed(1)}%</strong>
              <small>Geant4 {(latestBattery.shower_fragmentation.gap_fraction_truth * 100).toFixed(1)}%</small>
            </div>
            <div className="stat-card">
              <span>Disconnected fragments</span>
              <strong>{latestBattery.shower_fragmentation.connected_components_generated.toFixed(1)}</strong>
              <small>Geant4 {latestBattery.shower_fragmentation.connected_components_truth.toFixed(1)}</small>
            </div>
            <div className="stat-card">
              <span>Radial RMS (mm)</span>
              <strong>{latestBattery.radial_rms_mm.generated.toFixed(1)}</strong>
              <small>Geant4 {latestBattery.radial_rms_mm.truth.toFixed(1)}</small>
            </div>
            <div className="stat-card">
              <span>Longitudinal profile L1</span>
              <strong>{(latestBattery.mean_longitudinal_profile_relative_l1 * 100).toFixed(1)}%</strong>
              <small>relative to Geant4 shape</small>
            </div>
          </div>
          <p className="in-progress-status-line">{latestBattery.scientific_status}</p>
        </div>
      )}
      <p className="in-progress-updated">Refreshed {new Date(data.updated_utc).toLocaleString()}</p>
    </section>
  );
}
